import React from 'react';
import styles from './UserMessage.module.css';
import type { ChatMessage } from '../../types/foreman';
import { ThreadImage } from '../ThreadImage';

interface UserMessageProps {
  message: ChatMessage;
  /** True when the active thread is visible to more than one person (shared
   *  list, org-wide, or owned by someone else). Author chips only render in
   *  shared threads — private threads look exactly as they always have. */
  isSharedThread?: boolean;
  /** Logged-in username — used to collapse the author chip to "You". */
  currentUsername?: string;
}

function formatTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Author chip for shared threads. Three states:
 *  - own message → "You" (plain)
 *  - other user, stamp verified by the auth server → their name (plain)
 *  - other user, unverified/missing stamp → muted name + tooltip; the name is
 *    only what the sender's device claimed, so we visually hedge it
 *  - no [SENDER] tag at all (history predating attribution) → muted
 *    "sender not recorded" */
function SenderChip({ message, currentUsername }: { message: ChatMessage; currentUsername?: string }) {
  const info = message.senderInfo;
  if (!info) {
    return <span className={`${styles.sender} ${styles.senderMuted}`}>sender not recorded</span>;
  }
  if (currentUsername && info.username === currentUsername) {
    return <span className={styles.sender}>You</span>;
  }
  const displayName = info.name || info.username;
  if (message.senderVerified === true) {
    return <span className={styles.sender}>{displayName}</span>;
  }
  return (
    <span
      className={`${styles.sender} ${styles.senderMuted}`}
      title="name as reported by sender's device — not verified"
    >
      {displayName}
    </span>
  );
}

const UserMessage: React.FC<UserMessageProps> = ({ message, isSharedThread, currentUsername }) => {
  return (
    <div className={styles.wrapper}>
      <div className={styles.bubble}>
        <div className={styles.content}>{message.content}</div>
        {message.images && message.images.length > 0 && (
          <div className={styles.images}>
            {message.images.map((img, i) => (
              <ThreadImage
                key={`${img.b64len}-${i}`}
                sessionKey={message.sessionId}
                b64len={img.b64len}
                mimeType={img.mimeType}
              />
            ))}
          </div>
        )}
        <div className={styles.footer}>
          {isSharedThread ? (
            <SenderChip message={message} currentUsername={currentUsername} />
          ) : (
            <span className={styles.sender}>{message.sender}</span>
          )}
          <span className={styles.timestamp}>{formatTime(message.timestamp)}</span>
        </div>
      </div>
    </div>
  );
};

export default UserMessage;
